import { format } from 'date-fns/format';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDropdown from '../../components/GenericDropDown';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { GameConfig } from '../../models/GameConfig';
import { getAllActiveGameConfigs } from '../../redux/entities/gameEntity';
import { getPattiReport, savePattiReport } from '../../redux/entities/transactionEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { downloadPdf, PattiPdf } from './calculatorPdf';
import {
    applyPaint, ButtonContainer, Cell, CellColors, ColorBar, DAYS, DayKey, DayPanel, DIGITS, emptyExtras,
    ExtraBoxes, HeaderCell, Layout, PageHeading, Paint, parseColors, parseExtras, parsePoints, Sheet,
    SheetContainer, SheetExtras, SheetScroll, TotalCell,
    useDayDates, useNoZoomViewport, useUnsavedWarning,
} from './calculatorShared';

const Filters = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 0.5rem;

  > * {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 576px) {
    gap: 8px;
  }
`;

// Small grey patti number above each input
const PattiLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  line-height: 1.2;
  padding-top: 2px;

  @media (max-width: 576px) {
    font-size: 10px;
  }
`;

const PaintableCell = styled(Cell)<{ $painting: boolean }>`
  cursor: ${({ $painting }) => ($painting ? 'pointer' : 'auto')};
  input {
    cursor: ${({ $painting }) => ($painting ? 'pointer' : 'text')};
  }
`;

const EmptyHint = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: #666;
  border: 1px dashed #ccc;
  border-radius: 5px;
`;

// Patti numbers grouped by single digit (sum of digits mod 10), columns in 1..9,0 order
const PATTI_BY_DIGIT: Record<string, number[]> = {
    "1": [100, 119, 128, 137, 146, 155, 236, 245, 227, 290, 335, 344, 380, 399, 470, 489, 560, 579, 588, 669, 678, 777],
    "2": [110, 129, 138, 147, 156, 237, 246, 345, 390, 480, 570, 589, 679, 228, 255, 336, 499, 660, 688, 778, 444, 200],
    "3": [111, 120, 139, 148, 157, 238, 247, 256, 346, 490, 580, 670, 689, 166, 229, 337, 355, 445, 599, 779, 788, 300],
    "4": [130, 158, 149, 167, 239, 248, 257, 347, 356, 590, 680, 789, 112, 220, 266, 338, 446, 455, 699, 770, 888, 400],
    "5": [140, 168, 159, 230, 249, 258, 267, 348, 357, 456, 690, 780, 113, 122, 177, 339, 366, 447, 799, 889, 555, 500],
    "6": [150, 123, 169, 178, 240, 259, 268, 349, 358, 367, 457, 790, 114, 277, 330, 448, 466, 556, 880, 899, 222, 600],
    "7": [160, 124, 179, 250, 269, 278, 340, 359, 368, 458, 467, 890, 115, 133, 188, 223, 377, 449, 557, 566, 999, 700],
    "8": [170, 125, 134, 189, 260, 279, 350, 369, 378, 459, 468, 567, 116, 224, 233, 288, 440, 477, 558, 990, 666, 800],
    "9": [180, 126, 135, 234, 270, 289, 360, 379, 450, 469, 478, 568, 117, 144, 199, 225, 388, 559, 577, 667, 333, 900],
    "0": [118, 127, 136, 145, 190, 226, 235, 244, 280, 299, 334, 370, 389, 460, 479, 488, 550, 569, 578, 668, 677, 0],
};

const PATTI_COLUMNS: string[][] = DIGITS.map(d =>
    PATTI_BY_DIGIT[d].map(num => num.toString().padStart(3, '0'))
);
const ROW_COUNT = Math.max(...PATTI_COLUMNS.map(c => c.length));
const ALL_PATTIS = PATTI_COLUMNS.flat();

type PattiNumber = { value: string; points: string };

// Colours are keyed by patti number
interface PattiSheet {
    numbers: PattiNumber[];
    total: number;
    colors: CellColors;
    extras: SheetExtras;
}

const sumPoints = (numbers: PattiNumber[]) =>
    numbers.reduce((sum, n) => sum + parsePoints(n.points), 0);

// Full grid of patti numbers, optionally filled from a saved sheet.
// Older saves are a bare [{ value, points }] list; newer ones are { numbers, colors, extras }.
const buildPattiSheet = (saved?: any): PattiSheet => {
    const savedNumbers: PattiNumber[] = Array.isArray(saved) ? saved
        : Array.isArray(saved?.numbers) ? saved.numbers : [];
    const savedPoints = new Map(savedNumbers.map(n => [n.value, n.points]));
    const numbers = ALL_PATTIS.map(value => ({ value, points: savedPoints.get(value) || '' }));
    return {
        numbers,
        total: sumPoints(numbers),
        colors: parseColors(saved?.colors),
        extras: saved?.extras ? parseExtras(saved.extras) : emptyExtras(),
    };
};

const PattiReportPage = () => {
    const dispatch = useAppDispatch();
    const { showModalStrip } = useModalStrip();
    const gameConfigs = useAppSelector((state) => state.entities.gameReducer.activeGameConfigs);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [selectedGame, setSelectedGame] = useState<string>('');
    const [selectedResultTime, setSelectedResultTime] = useState<string>('');
    const [selectedDay, setSelectedDay] = useState<DayKey>('AAJ');
    const [sheets, setSheets] = useState<Partial<Record<DayKey, PattiSheet>>>({});
    const [dirtyDays, setDirtyDays] = useState<DayKey[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [paint, setPaint] = useState<Paint>(null);
    // Read inside async loads, so a late response never overwrites what the user typed
    const dirtyRef = useRef<DayKey[]>([]);
    dirtyRef.current = dirtyDays;

    useNoZoomViewport();
    useUnsavedWarning(dirtyDays.length > 0);
    const dayDates = useDayDates();

    useEffect(() => {
        const fetchGameConfigs = async () => {
            if ((!gameConfigs || gameConfigs.length === 0) && !isLoading && !hasFetched) {
                try {
                    setIsLoading(true);
                    await dispatch(getAllActiveGameConfigs()).unwrap();
                    setHasFetched(true);
                } catch (err) {
                    showModalStrip('danger', 'Failed to load game configurations', 5000);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchGameConfigs();
    }, [dispatch, gameConfigs, isLoading, hasFetched, showModalStrip]);

    const groupedGameConfigs = useMemo(() => gameConfigs.reduce((acc, config) => {
        const gameName = config?.game?.name;
        if (!acc[gameName]) {
            acc[gameName] = [];
        }
        acc[gameName].push(config);
        return acc;
    }, {} as Record<string, GameConfig[]>), [gameConfigs]);

    const resultTimeOptions = useMemo(() => (groupedGameConfigs[selectedGame] || []).map(config => ({
        value: config.resultTime.id.toString(),
        label: config.resultTime.resultTime,
    })), [groupedGameConfigs, selectedGame]);

    const gameId = groupedGameConfigs[selectedGame]?.[0]?.game?.id;
    const isReady = Boolean(gameId && selectedResultTime);

    // Switching game/result time replaces all three days; confirm if that drops typed numbers
    const confirmDiscard = () =>
        dirtyRef.current.length === 0 || window.confirm('You have unsaved numbers. Discard them?');

    const handleGameChange = (gameName: string) => {
        if (gameName === selectedGame || !confirmDiscard()) return;
        setSelectedGame(gameName);
        // Result times differ per game; clear the old choice
        setSelectedResultTime('');
    };

    const handleResultTimeChange = (resultTime: string) => {
        if (resultTime === selectedResultTime || !confirmDiscard()) return;
        setSelectedResultTime(resultTime);
    };

    // Resolves true if the day loaded; on failure the day starts as an empty sheet
    const loadDay = useCallback(async (day: DayKey, isCancelled: () => boolean): Promise<boolean> => {
        try {
            const response: any = await dispatch(getPattiReport({
                gameId,
                date: format(dayDates[day], 'yyyy-MM-dd'),
                resultTimeId: Number(selectedResultTime),
                type: 'Patti',
            })).unwrap();
            let saved = response?.reportSheetData ?? null;
            if (typeof saved === 'string') saved = JSON.parse(saved);
            if (!isCancelled() && !dirtyRef.current.includes(day)) {
                setSheets(prev => ({ ...prev, [day]: buildPattiSheet(saved) }));
            }
            return true;
        } catch (error) {
            if (!isCancelled() && !dirtyRef.current.includes(day)) {
                setSheets(prev => ({ ...prev, [day]: buildPattiSheet() }));
            }
            return false;
        }
    }, [dayDates, dispatch, gameId, selectedResultTime]);

    // Load all three days whenever game and result time are both chosen
    useEffect(() => {
        setSheets({});
        setDirtyDays([]);
        dirtyRef.current = [];
        if (!isReady) return;

        let cancelled = false;
        const isCancelled = () => cancelled;
        Promise.all(DAYS.map(d => loadDay(d.key, isCancelled))).then(results => {
            const failed = DAYS.filter((_, i) => !results[i]).map(d => d.key);
            if (!cancelled && failed.length) {
                showModalStrip('danger', `Could not load saved data for ${failed.join(', ')}`, 5000);
            }
        });
        return () => { cancelled = true; };
    }, [isReady, loadDay, showModalStrip]);

    const sheetData = sheets[selectedDay] || buildPattiSheet();
    const pointsByPatti = useMemo(
        () => new Map(sheetData.numbers.map(n => [n.value, n.points])),
        [sheetData]
    );

    const updateSheet = (change: (current: PattiSheet) => PattiSheet) => {
        setDirtyDays(prev => (prev.includes(selectedDay) ? prev : [...prev, selectedDay]));
        setSheets(prev => ({ ...prev, [selectedDay]: change(prev[selectedDay] || buildPattiSheet()) }));
    };

    const handlePointChange = (patti: string, value: string) => {
        // Only allow numeric values
        const numericValue = value.replace(/[^0-9]/g, '');
        updateSheet(current => {
            const numbers = current.numbers.map(n => (n.value === patti ? { ...n, points: numericValue } : n));
            return { ...current, numbers, total: sumPoints(numbers) };
        });
    };

    const handlePaint = (patti: string) => {
        if (paint === null) return;
        updateSheet(current => ({ ...current, colors: applyPaint(current.colors, patti, paint) }));
    };

    const handleExtraChange = (field: keyof SheetExtras, value: string) => {
        updateSheet(current => ({ ...current, extras: { ...current.extras, [field]: value } }));
    };

    const columnTotals = PATTI_COLUMNS.map(column =>
        column.reduce((sum, p) => sum + parsePoints(pointsByPatti.get(p)), 0)
    );

    const handleDownload = () => {
        const date = dayDates[selectedDay];
        const resultTime = resultTimeOptions.find(o => o.value === selectedResultTime)?.label || '';
        downloadPdf(
            <PattiPdf
                subtitle={`${selectedGame} ${resultTime} - ${selectedDay} ${format(date, 'dd MMM yyyy')}`}
                columns={PATTI_COLUMNS}
                rowCount={ROW_COUNT}
                pointsOf={patti => pointsByPatti.get(patti) || ''}
                columnTotals={columnTotals}
                total={sheetData.total}
                colors={sheetData.colors}
                extras={sheetData.extras}
            />,
            `patti-${selectedGame}-${selectedDay}-${format(date, 'yyyy-MM-dd')}.pdf`.replace(/\s+/g, '_')
        ).catch(() => showModalStrip('danger', 'Failed to create PDF', 5000));
    };

    const handleSubmit = () => {
        if (isSaving) return;
        if (!isReady) {
            showModalStrip('warning', 'Please select game and result time', 2000);
            return;
        }

        const day = selectedDay;
        setIsSaving(true);
        dispatch(savePattiReport({
            gameId,
            date: format(dayDates[day], 'yyyy-MM-dd'),
            resultTimeId: Number(selectedResultTime),
            type: 'Patti',
            reportSheetData: { numbers: sheetData.numbers, colors: sheetData.colors, extras: sheetData.extras },
        }))
            .unwrap()
            .then(() => {
                setDirtyDays(prev => prev.filter(d => d !== day));
                showModalStrip('success', `${day} Patti report saved successfully`, 2000);
            })
            .catch((err: unknown) => {
                showModalStrip('danger', typeof err === 'string' ? err : 'Failed to save report', 5000);
            })
            .finally(() => setIsSaving(false));
    };

    return (
        <Container fluid>
            <PageHeading>Patti Calculator</PageHeading>

            <Filters>
                <GenericDropdown
                    label="Game"
                    options={Object.keys(groupedGameConfigs).map((gameName) => ({
                        value: gameName,
                        label: gameName,
                    }))}
                    onChange={handleGameChange}
                    value={selectedGame}
                />
                <GenericDropdown
                    label="Result Time"
                    options={resultTimeOptions}
                    onChange={handleResultTimeChange}
                    value={selectedResultTime}
                    disabled={!selectedGame}
                />
            </Filters>

            <Layout>
                <SheetContainer>
                    {!isReady ? (
                        <EmptyHint>Select a game and result time to start.</EmptyHint>
                    ) : (
                        <>
                            <ColorBar paint={paint} onChange={setPaint} />
                            <SheetScroll>
                                <Sheet>
                                    <thead>
                                        <tr>
                                            {DIGITS.map(d => (
                                                <HeaderCell as="th" key={d}>{d}</HeaderCell>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: ROW_COUNT }, (_, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {PATTI_COLUMNS.map((column, colIndex) => {
                                                    const patti = column[rowIndex];
                                                    return (
                                                        <PaintableCell
                                                            key={colIndex}
                                                            $painting={paint !== null && Boolean(patti)}
                                                            style={{ background: patti ? sheetData.colors[patti] : undefined }}
                                                            onClick={() => patti && handlePaint(patti)}
                                                        >
                                                            {patti && (
                                                                <>
                                                                    <PattiLabel>{patti}</PattiLabel>
                                                                    <input
                                                                        type="text"
                                                                        inputMode="numeric"
                                                                        pattern="[0-9]*"
                                                                        autoComplete="off"
                                                                        readOnly={paint !== null}
                                                                        aria-label={`Patti ${patti}`}
                                                                        value={pointsByPatti.get(patti) || ''}
                                                                        onChange={(e) => handlePointChange(patti, e.target.value)}
                                                                    />
                                                                </>
                                                            )}
                                                        </PaintableCell>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                        <tr>
                                            {columnTotals.map((total, colIndex) => (
                                                <TotalCell key={colIndex}>{total}</TotalCell>
                                            ))}
                                        </tr>
                                        <tr>
                                            {DIGITS.map(d => (
                                                <HeaderCell key={d}>{d}</HeaderCell>
                                            ))}
                                        </tr>
                                        <tr>
                                            <TotalCell colSpan={DIGITS.length}>TOTAL: {sheetData.total}</TotalCell>
                                        </tr>
                                    </tbody>
                                </Sheet>
                            </SheetScroll>
                            <ExtraBoxes extras={sheetData.extras} onChange={handleExtraChange} />
                            <ButtonContainer>
                                <GenericButton
                                    label="Download PDF"
                                    type="secondary"
                                    onClick={handleDownload}
                                />
                                <GenericButton
                                    label={isSaving ? 'Saving...' : `Save ${selectedDay}`}
                                    onClick={handleSubmit}
                                    disabled={isSaving}
                                />
                            </ButtonContainer>
                        </>
                    )}
                </SheetContainer>

                <DayPanel
                    dayDates={dayDates}
                    totals={Object.fromEntries(DAYS.map(d => [d.key, sheets[d.key]?.total ?? 0]))}
                    dirtyDays={dirtyDays}
                    selectedDay={selectedDay}
                    onSelect={setSelectedDay}
                />
            </Layout>
        </Container>
    );
};

export default PattiReportPage;
